import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Pencil, Plus, Trash2, Wallet, X } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panel budgets par exercice d'un service (US-SVC-06).
 * Affiche barres de progression et alertes seuils 80% / 100%.
 */

interface BudgetRow {
  id: string;
  service_id: string;
  exercice: string;
  budget_alloue: number;
  budget_preventif: number | null;
  budget_curatif: number | null;
  budget_amelioratif: number | null;
  budget_consomme: number;
  budget_engage: number;
  centre_cout: string | null;
  taux_consommation: number | null;
  taux_engagement: number | null;
  reste_a_depenser: number;
}

interface BudgetFormState {
  exercice: string;
  budget_alloue: number;
  budget_preventif: number;
  budget_curatif: number;
  budget_amelioratif: number;
  budget_consomme: number;
  budget_engage: number;
  centre_cout: string;
}

function emptyBudgetForm(year: number): BudgetFormState {
  return {
    exercice: String(year),
    budget_alloue: 0,
    budget_preventif: 0,
    budget_curatif: 0,
    budget_amelioratif: 0,
    budget_consomme: 0,
    budget_engage: 0,
    centre_cout: '',
  };
}

export function BudgetsPanel({ serviceId }: { serviceId: string }) {
  const { can } = usePermissions();
  const canEdit = can('services', 'update');

  const [items, setItems] = useState<BudgetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<BudgetFormState | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: BudgetRow[] }>(`/services/${serviceId}/budgets`);
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer ce budget ?')) return;
    try {
      await api.delete(`/services/budgets/${id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  function startCreate() {
    const year = new Date().getFullYear();
    // Évite de proposer un exercice déjà existant.
    const existingExercices = new Set(items.map((i) => i.exercice));
    let target = year;
    while (existingExercices.has(String(target))) target += 1;
    setEditing(emptyBudgetForm(target));
  }

  function startEdit(b: BudgetRow) {
    setEditing({
      exercice: b.exercice,
      budget_alloue: b.budget_alloue,
      budget_preventif: b.budget_preventif ?? 0,
      budget_curatif: b.budget_curatif ?? 0,
      budget_amelioratif: b.budget_amelioratif ?? 0,
      budget_consomme: b.budget_consomme,
      budget_engage: b.budget_engage,
      centre_cout: b.centre_cout ?? '',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={startCreate}
            className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nouvel exercice
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucun budget enregistré.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((b) => (
            <BudgetCard
              key={b.id}
              budget={b}
              canEdit={canEdit}
              onEdit={() => startEdit(b)}
              onDelete={() => handleDelete(b.id)}
            />
          ))}
        </ul>
      )}

      {editing && (
        <BudgetFormModal
          serviceId={serviceId}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function BudgetCard({
  budget,
  canEdit,
  onEdit,
  onDelete,
}: {
  budget: BudgetRow;
  canEdit: boolean;
  onEdit(): void;
  onDelete(): void;
}) {
  const taux = budget.taux_consommation ?? 0;
  const pct = Math.min(taux * 100, 999);
  const barColor =
    taux > 1
      ? 'bg-red-500'
      : taux >= 0.8
        ? 'bg-orange-500'
        : taux >= 0.6
          ? 'bg-orange-400'
          : 'bg-green-500';
  const barWidth = Math.min(taux * 100, 100);
  const overflow = taux > 1;
  const alert80 = taux >= 0.8 && taux <= 1;

  return (
    <li className="rounded-lg border border-gray-200 p-3 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 flex items-center">
            <Wallet className="w-4 h-4 mr-1.5 text-primary-600" />
            Exercice {budget.exercice}
            {budget.centre_cout && (
              <span className="ml-2 text-xs text-gray-500 font-normal">
                · Centre {budget.centre_cout}
              </span>
            )}
            {overflow && (
              <span className="ml-2 inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-red-100 text-red-800 border-red-200">
                Dépassement budgétaire
              </span>
            )}
            {alert80 && (
              <span className="ml-2 inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-orange-100 text-orange-800 border-orange-200">
                Seuil 80% dépassé
              </span>
            )}
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center space-x-1">
            <button
              onClick={onEdit}
              className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
              aria-label="Modifier"
              title="Modifier"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded text-red-600 hover:bg-red-50"
              aria-label="Supprimer"
              title="Supprimer"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="mt-3">
        <div className="flex items-baseline justify-between text-xs text-gray-600 mb-1">
          <span>Consommé {formatMoney(budget.budget_consomme)}</span>
          <span>Alloué {formatMoney(budget.budget_alloue)}</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${barWidth}%` }} />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Taux de consommation : {(taux * 100).toFixed(1)}% — Reste à dépenser :{' '}
          <span className={budget.reste_a_depenser < 0 ? 'text-red-600 font-medium' : ''}>
            {formatMoney(budget.reste_a_depenser)}
          </span>
        </p>
      </div>

      <dl className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-xs">
        <Row label="Préventif" value={formatMoney(budget.budget_preventif ?? 0)} />
        <Row label="Curatif" value={formatMoney(budget.budget_curatif ?? 0)} />
        <Row label="Amélioratif" value={formatMoney(budget.budget_amelioratif ?? 0)} />
        <Row label="Engagé" value={formatMoney(budget.budget_engage)} />
        <Row
          label="Taux engagement"
          value={
            budget.taux_engagement !== null ? `${(budget.taux_engagement * 100).toFixed(1)}%` : '—'
          }
        />
        <Row label="Pct cumulé" value={`${pct.toFixed(1)}%`} />
      </dl>
    </li>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 font-medium">{value}</dd>
    </div>
  );
}

function formatMoney(v: number): string {
  return v.toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

// ─────────────────────────────────────────────────────────────────────────────

function BudgetFormModal({
  serviceId,
  initial,
  onClose,
  onSaved,
}: {
  serviceId: string;
  initial: BudgetFormState;
  onClose(): void;
  onSaved(): void;
}) {
  const [state, setState] = useState<BudgetFormState>(initial);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function update<K extends keyof BudgetFormState>(key: K, value: BudgetFormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.put(`/services/${serviceId}/budgets`, {
        exercice: state.exercice,
        budget_alloue: state.budget_alloue,
        budget_preventif: state.budget_preventif,
        budget_curatif: state.budget_curatif,
        budget_amelioratif: state.budget_amelioratif,
        budget_consomme: state.budget_consomme,
        budget_engage: state.budget_engage,
        centre_cout: state.centre_cout || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">
              Budget — exercice {state.exercice}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exercice * (YYYY)
                </label>
                <input
                  type="text"
                  pattern="\d{4}"
                  value={state.exercice}
                  onChange={(e) => update('exercice', e.target.value)}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Centre de coût
                </label>
                <input
                  type="text"
                  value={state.centre_cout}
                  onChange={(e) => update('centre_cout', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget alloué (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={state.budget_alloue}
                  onChange={(e) => update('budget_alloue', Number(e.target.value))}
                  className={inputClass}
                />
              </div>

              <fieldset className="col-span-2 border border-gray-200 rounded-lg p-3">
                <legend className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
                  Ventilation
                </legend>
                <div className="grid grid-cols-3 gap-3">
                  <NumberField
                    label="Préventif"
                    value={state.budget_preventif}
                    onChange={(v) => update('budget_preventif', v)}
                  />
                  <NumberField
                    label="Curatif"
                    value={state.budget_curatif}
                    onChange={(v) => update('budget_curatif', v)}
                  />
                  <NumberField
                    label="Amélioratif"
                    value={state.budget_amelioratif}
                    onChange={(v) => update('budget_amelioratif', v)}
                  />
                </div>
              </fieldset>

              <NumberField
                label="Consommé (€)"
                value={state.budget_consomme}
                onChange={(v) => update('budget_consomme', v)}
              />
              <NumberField
                label="Engagé (€)"
                value={state.budget_engage}
                onChange={(v) => update('budget_engage', v)}
              />
            </div>
          </form>

          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange(v: number): void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={inputClass}
      />
    </div>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
